"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


/**
 * Shopify Orders Page
 *
 * Uses the CRM Abstraction Layer:
 * - Supports multiple CRM providers (Kommo, HubSpot, etc.)
 * - Uses crm_bindings with scope_type='shopify_orders'
 * - Provider-agnostic UI
 */

import { useEffect, useState, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { INTEGRATIONS_PERMISSIONS, SHOPIFY_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { usePermissions } from "@/hooks/use-permissions"
import {
  RefreshCcw,
  ShoppingBag,
  Settings,
  Loader2,
  Package,
  DollarSign,
  User,
  Calendar,
  CheckCircle2,
  Building2,
  Plus,
  Link2,
  Unplug,
  AlertCircle,
  Eye,
  Search,
  X,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useShopify, type ShopifyOrder, type OrderSortField, type SortOrder } from "@/hooks/use-shopify"
import {
  useCrmBinding,
  type CrmStage,
} from "@/hooks/integrations/use-crm-binding"
import { useCrmIntegrations } from "@/hooks/integrations/use-crm-integrations"
import type { CrmProviderType } from "@/lib/crm/types"
import { AddCrmIntegrationDialog, CrmProviderLogo } from "@/components/crm"
import { cn } from "@/lib/utils"
import { Pagination } from "@/components/ui/pagination"
import { useAuth } from "@/hooks/use-auth"
import { OrderDetailDrawer } from "./order-detail-drawer"
import { isValidShopDomain } from "@/lib/shopify/shop-domain"
import { InitialSyncHint } from "./initial-sync-hint"
import { useInitialSyncPart, useSyncCompletionRefresh } from "@/hooks/queries/shopify-initial-sync"
import type { SyncPartState } from "@/server-actions/shopify/sync-status"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






export function ShopifyOrdersPage() {
  const {locale: uiFormattingLocale} = useUiControlLabels()




  const tUi = useTranslations("Shopify.ShopifyOrdersPage")
  const tCommonUi = useTranslations("Common")

  const [activeTab, setActiveTab] = useState<"orders" | "settings">("orders")
  const [showAddCrmDialog, setShowAddCrmDialog] = useState(false)

  // Deep-linking: order detail drawer
  const [detailOrderId, setDetailOrderId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null
    return new URLSearchParams(window.location.search).get("orderId")
  })
  const [detailIntegrationId, setDetailIntegrationId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null
    return new URLSearchParams(window.location.search).get("integrationId")
  })
  // The Shopify admin link names only the shop; it maps to an integration of the signed-in tenant alone.
  const [detailShop, setDetailShop] = useState<string | null>(() => {
    if (typeof window === "undefined") return null
    const shop = new URLSearchParams(window.location.search).get("shop")
    return shop && isValidShopDomain(shop) ? shop : null
  })
  const { can } = usePermissions()
  const initialSync = useInitialSyncPart("orders")

  // Read URL params once on mount (product filter + date range)
  const urlParams = useMemo(() => {
    if (typeof window === "undefined") return { productFilter: null, dateRange: null }
    const params = new URLSearchParams(window.location.search)

    const monthStartUtc = params.get("month_start_utc")
    const monthEndUtc = params.get("month_end_utc")
    const dateRange = monthStartUtc || monthEndUtc ? { monthStartUtc, monthEndUtc } : null

    const integrationId = params.get("integration_id")

    // order_ids takes precedence over product_ids
    const rawOrderIds = params.get("order_ids")
    if (rawOrderIds && integrationId) {
      const orderIds = rawOrderIds.split(",").filter(Boolean)
      if (orderIds.length > 0) {
        return {
          productFilter: null,
          orderFilter: { orderIds, integrationId },
          dateRange,
        }
      }
    }

    const rawIds = params.get("product_ids")
    if (!rawIds || !integrationId) return { productFilter: null, orderFilter: null, dateRange }
    const productIds = rawIds.split(",").map(Number).filter(Number.isFinite)
    if (productIds.length === 0) return { productFilter: null, orderFilter: null, dateRange }

    return {
      productFilter: { productIds, integrationId },
      orderFilter: null,
      dateRange,
    }
  }, [])

  const isProductFilterMode = urlParams.productFilter !== null || urlParams.orderFilter !== null

  // State for product-filtered orders
  const [pfOrders, setPfOrders] = useState<ShopifyOrder[]>([])
  const [pfLoading, setPfLoading] = useState(false)
  const [pfPage, setPfPage] = useState(1)
  const [pfPageSize, setPfPageSize] = useState(25)
  const [pfTotal, setPfTotal] = useState(0)
  const [pfSearchApplied, setPfSearchApplied] = useState("")
  const [pfSortBy, setPfSortBy] = useState<OrderSortField>("created_at_shopify")
  const [pfSortOrder, setPfSortOrder] = useState<SortOrder>("desc")

  // For creating CRM leads from orders (super_user only)
  const { fetchWithAuth, user } = useAuth()
  const isSuperUser = user?.role === 'super_user'
  const userTimezone = user?.timezone && typeof user.timezone === "string" && user.timezone.trim() !== "" ? user.timezone : "UTC"

  // Fetch product-filtered orders
  const fetchProductFilteredOrders = useCallback(async (
    page: number,
    pageSize: number,
    search: string,
    sortBy: OrderSortField = pfSortBy,
    sortOrder: SortOrder = pfSortOrder,
  ) => {
    const filter = urlParams.productFilter ?? urlParams.orderFilter
    if (!filter) return
    setPfLoading(true)
    try {
      const params = new URLSearchParams({
        integration_id: filter.integrationId,
        page: String(page),
        pageSize: String(pageSize),
      })
      if ("productIds" in filter) {
        params.set("product_ids", filter.productIds.join(","))
      }
      if ("orderIds" in filter) {
        params.set("order_ids", filter.orderIds.join(","))
      }
      if (urlParams.dateRange?.monthStartUtc) params.set("month_start_utc", urlParams.dateRange.monthStartUtc)
      if (urlParams.dateRange?.monthEndUtc) params.set("month_end_utc", urlParams.dateRange.monthEndUtc)
      if (search) params.set("search", search)
      params.set("sort_by", sortBy)
      params.set("sort_order", sortOrder)

      const res = await fetchWithAuth(`/api/integrations/shopify/orders?${params}`)
      if (res.ok) {
        const data = await res.json()
        setPfOrders(data.orders ?? [])
        setPfTotal(data.total ?? 0)
      }
    } catch (err) {
      console.error("[OrdersPage] Product filter fetch error:", err)
    } finally {
      setPfLoading(false)
    }
  }, [urlParams, fetchWithAuth, pfSortBy, pfSortOrder])

  useEffect(() => {
    if (isProductFilterMode) {
      void fetchProductFilteredOrders(pfPage, pfPageSize, pfSearchApplied)
    }
  }, [isProductFilterMode, pfPage, pfPageSize, pfSearchApplied, fetchProductFilteredOrders])

  const handleClearProductFilter = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete("product_ids")
    url.searchParams.delete("order_ids")
    url.searchParams.delete("integration_id")
    url.searchParams.delete("month_start_utc")
    url.searchParams.delete("month_end_utc")
    window.location.href = url.toString()
  }

  const [leadAssociations, setLeadAssociations] = useState<Record<string, string>>({}) // orderId -> leadId
  const [creatingLeadForOrder, setCreatingLeadForOrder] = useState<string | null>(null)

  // For viewing raw order data from Shopify API (super_user only)
  const [viewingRawOrderId, setViewingRawOrderId] = useState<string | null>(null)
  const [rawOrderData, setRawOrderData] = useState<unknown>(null)
  const [rawOrderDialogOpen, setRawOrderDialogOpen] = useState(false)
  const [fetchingRawOrder, setFetchingRawOrder] = useState<string | null>(null)

  const {
    activeIntegrations,
    orders,
    ordersPage,
    ordersPageSize,
    ordersTotal,
    ordersSearch,
    isLoading: isLoadingShopify,
    ordersLoading,
    hasIntegration,
    refreshOrders,
    goToOrdersPage,
    changeOrdersPageSize,
    searchOrders,
    clearOrdersSearch,
    ordersSortBy,
    ordersSortOrder,
    sortOrders,
  } = useShopify(isProductFilterMode ? undefined : urlParams.dateRange ?? undefined)

  const detailShopIntegrationId = activeIntegrations.find((integration) => integration.shop_domain === detailShop)?.id ?? null
  const detailShopMissing = detailShop !== null && !isLoadingShopify && !detailShopIntegrationId

  // The orders list is not React Query, so the status poll cannot invalidate it: without
  // this the table would fall back to "no orders" the moment the sync finishes.
  useSyncCompletionRefresh("orders", refreshOrders)

  // Local state for search input
  const [searchInput, setSearchInput] = useState("")

  // Unified view: pick product-filter data or normal data
  const displayOrders = isProductFilterMode ? pfOrders : orders
  const displayLoading = isProductFilterMode ? pfLoading : ordersLoading
  const displayTotal = isProductFilterMode ? pfTotal : ordersTotal
  const displayPage = isProductFilterMode ? pfPage : ordersPage
  const displayPageSize = isProductFilterMode ? pfPageSize : ordersPageSize
  const displaySearch = isProductFilterMode ? pfSearchApplied : ordersSearch

  const handlePageChange = isProductFilterMode ? setPfPage : goToOrdersPage
  const handlePageSizeChange = isProductFilterMode
    ? (size: number) => { setPfPageSize(size); setPfPage(1) }
    : changeOrdersPageSize
  const handleSearch = isProductFilterMode
    ? (q: string) => { setPfSearchApplied(q); setPfPage(1) }
    : searchOrders
  const handleClearSearch = isProductFilterMode
    ? () => { setPfSearchApplied(""); setPfPage(1) }
    : clearOrdersSearch

  const displaySortBy = isProductFilterMode ? pfSortBy : ordersSortBy
  const displaySortOrder = isProductFilterMode ? pfSortOrder : ordersSortOrder
  const handleSort = isProductFilterMode
    ? (column: OrderSortField) => {
        const newOrder: SortOrder = column === pfSortBy ? (pfSortOrder === "asc" ? "desc" : "asc") : "desc"
        setPfSortBy(column)
        setPfSortOrder(newOrder)
        setPfPage(1)
        fetchProductFilteredOrders(1, pfPageSize, pfSearchApplied, column, newOrder)
      }
    : sortOrders

  // Local state for selected Shopify integration
  const [selectedShopifyIntegrationId, setSelectedShopifyIntegrationId] = useState<string>("")

  // CRM binding for the selected Shopify integration
  const {
    binding: crmBinding,
    integrations: crmIntegrations,
    pipelines,
    selectedIntegration: selectedCrmIntegration,
    isLoading: isLoadingCrm,
    isLoadingPipelines,
    isSaving,
    error: crmError,
    loadBinding,
    loadIntegrations: loadCrmIntegrations,
    saveBinding,
    updateBindingConfig,
    getStagesForPipeline,
  } = useCrmBinding({
    scopeType: "shopify_orders",
    scopeId: selectedShopifyIntegrationId,
    autoLoad: false, // We'll load manually when Shopify integration is selected
  })

  // CRM integrations hook for adding new integrations
  const {
    providers: crmProviders,
    isConnecting,
    connectIntegration,
    prepareOAuthInstall,
    error: connectError,
    clearError: clearConnectError,
    refreshIntegrations: refreshCrmIntegrations,
  } = useCrmIntegrations({ autoLoad: true })

  // Load CRM binding when Shopify integration is selected
  useEffect(() => {
    if (selectedShopifyIntegrationId) {
      loadBinding()
      loadCrmIntegrations()
    }
  }, [selectedShopifyIntegrationId, loadBinding, loadCrmIntegrations])

  // Auto-select the linked shop's integration, else the first one
  useEffect(() => {
    if (activeIntegrations.length > 0 && !selectedShopifyIntegrationId) {
      setSelectedShopifyIntegrationId(detailShopIntegrationId ?? activeIntegrations[0].id)
    }
  }, [activeIntegrations, selectedShopifyIntegrationId, detailShopIntegrationId])

  // A named shop never falls back to another store's integration.
  const drawerIntegrationId = detailIntegrationId || (detailShop ? detailShopIntegrationId : selectedShopifyIntegrationId)

  // Get stages for current pipeline
  const [pipelineStages, setPipelineStages] = useState<CrmStage[]>([])

  useEffect(() => {
    if (crmBinding?.config?.pipeline_id) {
      const stages = getStagesForPipeline(crmBinding.config.pipeline_id)
      setPipelineStages(stages)
    } else {
      setPipelineStages([])
    }
  }, [crmBinding?.config?.pipeline_id, pipelines, getStagesForPipeline])

  // Computed: CRM is enabled
  const isCrmEnabled = crmBinding && crmBinding.config?.enabled !== false

  // Filter active stages
  const activeStages = pipelineStages.filter((s) => !s.isArchived)

  // Load lead associations for current orders (super_user only, and only if CRM is connected)
  useEffect(() => {
    if (!isSuperUser || crmIntegrations.length === 0 || displayOrders.length === 0) {
      setLeadAssociations({})
      return
    }

    const loadAssociations = async () => {
      try {
        const orderIds = displayOrders.map(o => o.shopify_order_id)
        const response = await fetchWithAuth(`/api/integrations/shopify/orders/associations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderIds }),
        })

        if (response.ok) {
          const data = await response.json()
          const assocMap: Record<string, string> = {}
          data.associations?.forEach((a: { shopify_order_id: string; kommo_lead_id: string }) => {
            assocMap[a.shopify_order_id] = a.kommo_lead_id
          })
          setLeadAssociations(assocMap)
        }
      } catch (error) {
        console.error("Failed to load associations:", error)
      }
    }

    loadAssociations()
  }, [isSuperUser, crmIntegrations.length, displayOrders, fetchWithAuth])

  // Handlers
  const handleCreateLead = async (orderId: string, integrationId: string) => {
    setCreatingLeadForOrder(orderId)
    try {
      const response = await fetchWithAuth(`/api/integrations/shopify/orders/create-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, integrationId }),
      })

      const data = await response.json()

      if (response.ok) {
        alert("CRM lead created successfully!")
        setLeadAssociations(prev => ({ ...prev, [orderId]: data.leadId }))
      } else {
        alert(data.error || "Failed to create lead")
      }
    } catch (error) {
      alert("Error creating lead")
      console.error(error)
    } finally {
      setCreatingLeadForOrder(null)
    }
  }

  // Fetch raw order data from Shopify API
  const handleFetchRawOrder = async (orderId: string, integrationId: string) => {
    setFetchingRawOrder(orderId)
    try {
      const response = await fetchWithAuth(`/api/integrations/shopify/orders/fetch-live`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, integrationId }),
      })

      const data = await response.json()

      if (response.ok) {
        // Show webhook-compatible REST format (transformed from GraphQL)
        setRawOrderData(data.order)
        setViewingRawOrderId(orderId)
        setRawOrderDialogOpen(true)
      } else {
        alert(data.error || "Failed to fetch order from Shopify")
      }
    } catch (error) {
      alert("Error fetching order from Shopify")
      console.error(error)
    } finally {
      setFetchingRawOrder(null)
    }
  }

  const handleCrmIntegrationChange = async (integrationId: string) => {
    if (integrationId === "none") {
      // Disable CRM for this Shopify integration
      if (crmBinding) {
        await updateBindingConfig({ enabled: false })
      } else if (crmIntegrations.length > 0) {
        await saveBinding(crmIntegrations[0].id, { enabled: false })
      }
      return
    }

    // Create/update binding with new CRM integration
    await saveBinding(integrationId, {
      enabled: true,
      pipeline_id: undefined,
      shopify: {
        stage_id: undefined,
        show_shipping_address: false,
        show_product_properties: false,
      },
    })
  }

  const handlePipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      pipeline_id: pipelineId,
      shopify: {
        ...crmBinding?.config?.shopify,
        stage_id: undefined,
      },
    })
  }

  const handleStageChange = async (stageId: string) => {
    await updateBindingConfig({
      shopify: {
        ...crmBinding?.config?.shopify,
        stage_id: stageId,
      },
    })
  }

  const handleShippingAddressToggle = async (checked: boolean) => {
    await updateBindingConfig({
      shopify: {
        ...crmBinding?.config?.shopify,
        show_shipping_address: checked,
      },
    })
  }

  const handleProductPropertiesToggle = async (checked: boolean) => {
    await updateBindingConfig({
      shopify: {
        ...crmBinding?.config?.shopify,
        show_product_properties: checked,
      },
    })
  }

  // Handler for connecting new CRM
  const handleConnectCrm = useCallback(
    async (
      provider: string,
      name: string,
      credentials: Record<string, unknown>,
      metadata?: Record<string, unknown>
    ) => {
      const result = await connectIntegration(provider as CrmProviderType, name, credentials, metadata)
      if (result.success) {
        await loadCrmIntegrations()
        await refreshCrmIntegrations()
      }
      return result
    },
    [connectIntegration, loadCrmIntegrations, refreshCrmIntegrations]
  )

  const getProviderDisplayName = (provider: string) => {
    const names: Record<string, string> = {
      kommo: "Kommo",
      hubspot: "HubSpot",
      salesforce: "Salesforce",
      pipedrive: "Pipedrive",
    }
    return names[provider] || provider
  }

  const getProviderInfo = useCallback(
    (providerId: string) => {
      return crmProviders.find((p) => p.id === providerId)
    },
    [crmProviders]
  )

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(uiFormattingLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTimezone,
      })
    } catch {
      return dateString
    }
  }

  const formatPrice = (price: number, currency: string) => {
    try {
      return new Intl.NumberFormat(uiFormattingLocale, {
        style: "currency",
        currency: currency || "USD",
      }).format(price)
    } catch {
      return `${price} ${currency}`
    }
  }

  const getStatusColor = (status: string, type: "financial" | "fulfillment") => {
    if (type === "financial") {
      switch (status?.toLowerCase()) {
        case "paid":
          return "bg-green-100 text-green-800"
        case "pending":
          return "bg-yellow-100 text-yellow-800"
        case "refunded":
          return "bg-red-100 text-red-800"
        default:
          return "bg-gray-100 text-gray-800"
      }
    } else {
      switch (status?.toLowerCase()) {
        case "fulfilled":
          return "bg-green-100 text-green-800"
        case "partial":
          return "bg-yellow-100 text-yellow-800"
        case "unfulfilled":
          return "bg-gray-100 text-gray-800"
        default:
          return "bg-gray-100 text-gray-800"
      }
    }
  }

  // Check if CRM settings are complete
  const isCrmConfigured = isCrmEnabled &&
    crmBinding?.config?.pipeline_id &&
    crmBinding?.config?.shopify?.stage_id

  if (!can(SHOPIFY_PERMISSIONS.VIEW)) {
    return (
      <div className="h-full w-full max-w-7xl mx-auto p-3 md:p-6 space-y-6">
        <Card className="w-full shadow-sm border-0">
          <CardHeader>
            <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                <span key={tUi("youDonTHavePermissionToAccessShopify")}>{tUi("youDonTHavePermissionToAccessShopify")}</span></AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full w-full max-w-7xl mx-auto p-3 md:p-6 space-y-6">
      <Card className="w-full shadow-sm border-0">
        <CardHeader className="space-y-4 pb-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ShoppingBag className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold text-gray-900"><span key={"Shopify"}>{"Shopify"}</span></CardTitle>
                <p className="text-sm text-gray-600 mt-1"><span key={tUi("manageYourShopifyStoreIntegrationsAndOrders")}>{tUi("manageYourShopifyStoreIntegrationsAndOrders")}</span></p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
             {can([INTEGRATIONS_PERMISSIONS.SHOPIFY_MANAGE]) && (
              <Button
                variant="outline"
                onClick={() => setActiveTab((t) => (t === "orders" ? "settings" : "orders"))}
                className="h-9 px-4 text-sm font-medium"
              >
                {activeTab === "orders" ? (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    <span key={tUi("settings")}>{tUi("settings")}</span></>
                ) : (
                  <><TranslationText>{tUi.rich("tag0Tag0BackToOrders", { tag0: () => (<Package className="w-4 h-4 mr-2" />) })}</TranslationText></>
                )}
              </Button>
            )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {isLoadingShopify ? (
              <Badge variant="secondary" className="text-xs px-3 py-1">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                <span><span key={tUi("loadingIntegrations")}>{tUi("loadingIntegrations")}</span></span>
              </Badge>
            ) : activeIntegrations.length === 0 ? (
              <Badge variant="secondary" className="text-xs px-3 py-1">
                <Package className="w-3 h-3 mr-1" />
                <span><span key={tUi("noActiveShopifyStores")}>{tUi("noActiveShopifyStores")}</span></span>
              </Badge>
            ) : (
              activeIntegrations.map((integration) => (
                <Badge
                  key={integration.id}
                  variant="outline"
                  className="text-xs px-3 py-1.5 bg-white/50 border-green-200"
                >
                  <ShoppingBag className="w-3 h-3 mr-1" />
                  <span className="font-medium">{integration.shop_domain}</span>
                </Badge>
              ))
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {activeTab === "orders" ? (
            <div className="space-y-4">
              {detailShopMissing && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="break-words">
                    {<span key={tUi("theStoreValue0IsNotConnectedToThis", { value0: String(detailShop) })}>{tUi("theStoreValue0IsNotConnectedToThis", { value0: String(detailShop) })}</span>}
                  </AlertDescription>
                </Alert>
              )}
              {isProductFilterMode && (
                <Alert>
                  <Filter className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between">
                    <span key={urlParams.orderFilter
                        ? tUi("showingValue0SpecificOrderS", { value0: String(urlParams.orderFilter.orderIds.length) })
                        : tUi("showingOrdersForValue0ProductS", { value0: String(urlParams.productFilter!.productIds.length) })}>
                      {urlParams.orderFilter
                        ? tUi("showingValue0SpecificOrderS", { value0: String(urlParams.orderFilter.orderIds.length) })
                        : tUi("showingOrdersForValue0ProductS", { value0: String(urlParams.productFilter!.productIds.length) })
                      }
                    </span>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handleClearProductFilter}>
                      <X className="w-3 h-3 mr-1" />
                      <span><span key={tUi("clearFilter")}>{tUi("clearFilter")}</span></span>
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900"><span key={tUi("orders")}>{tUi("orders")}</span></h3>
                </div>
                <div className="flex items-center gap-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={tUi("searchByOrder")}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearch(searchInput)
                        }
                      }}
                      className="pl-9 pr-8 w-[200px] h-9"
                    />
                    {(searchInput || displaySearch) && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchInput("")
                          handleClearSearch()
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {displayTotal} <span key={displayTotal === 1 ? tUi("order") : tUi("orders2")}>{displayTotal === 1 ? tUi("order") : tUi("orders2")}</span>
                    {displaySearch && <span className="ml-1 text-blue-600"><span key={tUi("filtered")}>{tUi("filtered")}</span></span>}
                  </div>
                </div>
              </div>

              {/* Only while integrations are still unknown: `hasIntegration()` resolves async,
                  and claiming "no integration" before it does makes a working store look
                  unconnected. `isLoadingShopify` alone would also blank the table on every
                  background refresh (tab focus, integration change). */}
              {isLoadingShopify && !hasIntegration() && !isProductFilterMode ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600"><span key={tUi("loadingOrders")}>{tUi("loadingOrders")}</span></span>
                </div>
              ) : !isProductFilterMode && !hasIntegration() ? (
                <div className="p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <div className="text-center">
                    <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2"><span key={tUi("noShopifyIntegration")}>{tUi("noShopifyIntegration")}</span></h3>
                    <p className="text-sm text-gray-600 mb-4">
                      <span key={tUi("connectYourShopifyStoreToViewAndManage")}>{tUi("connectYourShopifyStoreToViewAndManage")}</span></p>
                    <p className="text-xs text-gray-500"><span key={tUi("goToYourProfileSettingsToAddA")}>{tUi("goToYourProfileSettingsToAddA")}</span></p>
                  </div>
                </div>
              ) : displayLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600"><span key={tUi("loadingOrders")}>{tUi("loadingOrders")}</span></span>
                </div>
              ) : displayTotal === 0 ? (
                <div className="p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <div className="text-center">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {emptyStateHeading(tUi, isProductFilterMode ? null : initialSync)}
                    </h3>
                    <div className="text-sm text-gray-600 mb-4">
                      {isProductFilterMode ? (
                        tUi("noOrdersFoundForTheSelectedProductS")
                      ) : initialSync ? (
                        <InitialSyncHint part="orders" />
                      ) : (
                        tUi("noOrdersHaveBeenSyncedFromYourShopify")
                      )}
                    </div>
                    {!isProductFilterMode && initialSync?.status !== "error" && (
                      <Button onClick={refreshOrders} variant="outline" size="sm">
                        <RefreshCcw className="w-4 h-4 mr-2" />
                        <span><span key={tUi("refreshOrders")}>{tUi("refreshOrders")}</span></span>
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <ScrollArea className="h-[60vh]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[80px]">
                            <Button variant="ghost" onClick={() => handleSort("name")} className="h-8 px-2">
                              <span><span key={tUi("order2")}>{tUi("order2")}</span></span>
                              {displaySortBy === "name" ? (
                                displaySortOrder === "asc" ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </TableHead>
                          <TableHead className="w-[160px]"><span key={tUi("customer")}>{tUi("customer")}</span></TableHead>
                          <TableHead className="w-[120px]">
                            <Button variant="ghost" onClick={() => handleSort("total_price")} className="h-8 px-2">
                              <span><span key={tUi("total")}>{tUi("total")}</span></span>
                              {displaySortBy === "total_price" ? (
                                displaySortOrder === "asc" ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </TableHead>
                          <TableHead className="w-[80px]">
                            <Button variant="ghost" onClick={() => handleSort("financial_status")} className="h-8 px-2">
                              <span><span key={tUi("payment")}>{tUi("payment")}</span></span>
                              {displaySortBy === "financial_status" ? (
                                displaySortOrder === "asc" ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </TableHead>
                          <TableHead className="w-[160px]">
                            <Button variant="ghost" onClick={() => handleSort("created_at_shopify")} className="h-8 px-2">
                              <span><span key={tUi("date")}>{tUi("date")}</span></span>
                              {displaySortBy === "created_at_shopify" ? (
                                displaySortOrder === "asc" ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </TableHead>
                          {isSuperUser && <TableHead className="w-[160px]"><span key={tUi("actions")}>{tUi("actions")}</span></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayOrders.map((order) => (
                          <TableRow
                            key={`${order.integration_id}-${order.shopify_order_id}`}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => {
                              setDetailOrderId(order.shopify_order_id)
                              setDetailIntegrationId(order.integration_id)
                              const url = new URL(window.location.href)
                              url.searchParams.set("orderId", order.shopify_order_id)
                              url.searchParams.set("integrationId", order.integration_id)
                              window.history.replaceState({}, "", url.toString())
                            }}
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-gray-400" />
                                {order.name || order.shopify_order_id}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <User className="w-3 h-3 text-gray-400" />
                                  <span key={order.customer_first_name || order.customer_last_name
                                      ? `${order.customer_first_name || ""} ${order.customer_last_name || ""}`.trim()
                                      : tUi("unknown")} className="text-sm font-medium">
                                    {order.customer_first_name || order.customer_last_name
                                      ? `${order.customer_first_name || ""} ${order.customer_last_name || ""}`.trim()
                                      : tUi("unknown")}
                                  </span>
                                </div>
                                {order.customer_email && (
                                  <div className="text-xs text-gray-500">{order.customer_email}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3 text-gray-400" />
                                <span className="font-medium">{formatPrice(order.total_price, order.currency)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={cn(
                                  "text-xs font-medium",
                                  getStatusColor(order.financial_status, "financial"),
                                )}
                              >
                                {order.financial_status || tUi("unknown")}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                {formatDate(order.created_at_shopify)}
                              </div>
                            </TableCell>
                            {isSuperUser && (
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {/* View Raw from Shopify API */}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleFetchRawOrder(order.shopify_order_id, order.integration_id) }}
                                    disabled={fetchingRawOrder === order.shopify_order_id}
                                    className="h-7 px-2"
                                    title={tUi("viewRawOrderDataFromShopify")}
                                  >
                                    {fetchingRawOrder === order.shopify_order_id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Eye className="w-3 h-3" />
                                    )}
                                  </Button>

                                  {/* Create CRM Lead */}
                                  {crmIntegrations.length > 0 && (
                                    leadAssociations[order.shopify_order_id] ? (
                                      <Badge variant="secondary" className="text-xs">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        <span><span key={tUi("lead")}>{tUi("lead")}</span></span>
                                      </Badge>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); handleCreateLead(order.shopify_order_id, order.integration_id) }}
                                        disabled={creatingLeadForOrder === order.shopify_order_id}
                                        className="h-7 text-xs"
                                      >
                                        {creatingLeadForOrder === order.shopify_order_id ? (
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        ) : (
                                          <Plus className="w-3 h-3 mr-1" />
                                        )}
                                        <span><span key={tUi("lead")}>{tUi("lead")}</span></span>
                                      </Button>
                                    )
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  <Pagination
                    page={displayPage}
                    pageSize={displayPageSize}
                    total={displayTotal}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    disabled={displayLoading}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900"><span key={tUi("settings")}>{tUi("settings")}</span></h3>
              </div>

              {/* CRM Integration Settings */}
              <Card className="p-6 bg-white shadow-sm">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Link2 className="w-5 h-5 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-900"><span key={tUi("cRMIntegrationOptional")}>{tUi("cRMIntegrationOptional")}</span></h4>
                  </div>

                  {crmError && (
                    <Alert variant="destructive">
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription>{crmError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Shopify Store Selection */}
                  {activeIntegrations.length > 1 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium"><span key={tUi("shopifyStore")}>{tUi("shopifyStore")}</span></Label>
                      <Select
                        value={selectedShopifyIntegrationId}
                        onValueChange={setSelectedShopifyIntegrationId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={tUi("selectShopifyStore")} />
                        </SelectTrigger>
                        <SelectContent>
                          {activeIntegrations.map((integration) => (
                            <SelectItem key={integration.id} value={integration.id}>
                              {integration.shop_domain}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        <span key={tUi("cRMSettingsAreConfiguredPerShopifyStore")}>{tUi("cRMSettingsAreConfiguredPerShopifyStore")}</span></p>
                    </div>
                  )}

                  {isLoadingCrm ? (
                    <div className="flex items-center gap-2 p-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm text-gray-500"><span key={tUi("loadingCRMSettings")}>{tUi("loadingCRMSettings")}</span></span>
                    </div>
                  ) : crmIntegrations.length === 0 ? (
                    <div className="space-y-4">
                      <Alert className="border-blue-200 bg-blue-50">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <AlertDescription className="text-blue-800">
                          <span key={tUi("noCRMIntegrationsAvailableConnectACRMTo")}>{tUi("noCRMIntegrationsAvailableConnectACRMTo")}</span></AlertDescription>
                      </Alert>
                      <Button
                        variant="outline"
                        onClick={() => setShowAddCrmDialog(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        <span><span key={tUi("connectCRM")}>{tUi("connectCRM")}</span></span>
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        <span key={tUi("configureAutomaticLeadCreationInYourCRMWhen")}>{tUi("configureAutomaticLeadCreationInYourCRMWhen")}</span></p>

                      {/* CRM Connection Selection */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium"><span key={tUi("cRMConnection")}>{tUi("cRMConnection")}</span></Label>
                        <Select
                          value={isCrmEnabled ? crmBinding?.integrationId : "none"}
                          onValueChange={handleCrmIntegrationChange}
                          disabled={isSaving}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={tUi("selectCRMIntegration")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <div className="flex items-center gap-2">
                                <Unplug className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-500"><span key={tUi("noneDisabled")}>{tUi("noneDisabled")}</span></span>
                              </div>
                            </SelectItem>
                            {crmIntegrations.map((integration) => {
                              const providerInfo = getProviderInfo(integration.provider)
                              return (
                                <SelectItem key={integration.id} value={integration.id}>
                                  <div className="flex items-center gap-2">
                                    {providerInfo?.logoUrl ? (
                                      <CrmProviderLogo
                                        provider={integration.provider}
                                        logoUrl={providerInfo.logoUrl}
                                        size={16}
                                      />
                                    ) : (
                                      <div
                                        className={cn(
                                          "w-4 h-4 rounded-full",
                                          integration.isActive ? "bg-green-500" : "bg-gray-300"
                                        )}
                                      />
                                    )}
                                    <span>
                                      {integration.name}{" "}
                                      <span className="text-gray-400">
                                        ({getProviderDisplayName(integration.provider)})
                                      </span>
                                    </span>
                                  </div>
                                </SelectItem>
                              )
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Add another CRM button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddCrmDialog(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        <span><span key={tUi("addAnotherCRM")}>{tUi("addAnotherCRM")}</span></span>
                      </Button>

                      {/* Pipeline & Stage Selection - only when CRM is enabled */}
                      {isCrmEnabled && selectedCrmIntegration && (
                        <div className="space-y-4 pt-4 border-t">
                          {/* Connection Status */}
                          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-800"><TranslationText>{tUi.rich("connectedToValue0Value1", { value0: String(selectedCrmIntegration.name), value1: String(getProviderDisplayName(selectedCrmIntegration.provider)) })}</TranslationText></span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Pipeline */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium"><span key={tUi("pipeline")}>{tUi("pipeline")}</span></Label>
                              {isLoadingPipelines ? (
                                <div className="flex items-center gap-2 p-2">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span className="text-sm text-gray-500"><span key={tUi("loadingPipelines")}>{tUi("loadingPipelines")}</span></span>
                                </div>
                              ) : (
                                <Select
                                  value={crmBinding?.config?.pipeline_id || ""}
                                  onValueChange={handlePipelineChange}
                                  disabled={isSaving}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={tUi("selectPipeline")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {pipelines.map((pipeline) => (
                                      <SelectItem key={pipeline.id} value={pipeline.id}>
                                        {pipeline.name} <span>{pipeline.isDefault && "(Default)"}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>

                            {/* Stage */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium"><span key={tUi("stage")}>{tUi("stage")}</span></Label>
                              <Select
                                value={crmBinding?.config?.shopify?.stage_id || ""}
                                onValueChange={handleStageChange}
                                disabled={isSaving || !crmBinding?.config?.pipeline_id}
                              >
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={
                                      !crmBinding?.config?.pipeline_id
                                        ? tUi("selectPipelineFirst")
                                        : tUi("selectStage")
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {activeStages.map((stage) => (
                                    <SelectItem key={stage.id} value={stage.id}>
                                      <div className="flex items-center gap-2">
                                        {stage.color && (
                                          <div
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: stage.color }}
                                          />
                                        )}
                                        <span>{stage.name}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Lead Options - only show when pipeline and stage are configured */}
                          {isCrmConfigured && (
                            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                              <h5 className="text-sm font-medium text-gray-900 mb-3"><span key={tUi("leadOptions")}>{tUi("leadOptions")}</span></h5>
                              <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="show-shipping-address"
                                    checked={crmBinding?.config?.shopify?.show_shipping_address || false}
                                    onCheckedChange={handleShippingAddressToggle}
                                    disabled={isSaving}
                                  />
                                  <Label htmlFor="show-shipping-address" className="text-sm text-gray-700">
                                    <span key={tUi("showShippingAddressInLeadDetails")}>{tUi("showShippingAddressInLeadDetails")}</span></Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id="show-product-properties"
                                    checked={crmBinding?.config?.shopify?.show_product_properties || false}
                                    onCheckedChange={handleProductPropertiesToggle}
                                    disabled={isSaving}
                                  />
                                  <Label htmlFor="show-product-properties" className="text-sm text-gray-700">
                                    <span key={tUi("showProductPropertiesInLeadDetails")}>{tUi("showProductPropertiesInLeadDetails")}</span></Label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add CRM Integration Dialog */}
      <AddCrmIntegrationDialog
        open={showAddCrmDialog}
        onOpenChange={setShowAddCrmDialog}
        providers={crmProviders}
        onConnect={handleConnectCrm}
        onPrepareOAuth={prepareOAuthInstall}
        isConnecting={isConnecting}
        error={connectError}
        onClearError={clearConnectError}
      />

      {/* Raw Order Data Dialog */}
      <Dialog open={rawOrderDialogOpen} onOpenChange={setRawOrderDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              <span><span key={tUi("rawOrderDataFromShopify")}>{tUi("rawOrderDataFromShopify")}</span></span>
              {viewingRawOrderId && (
                <Badge variant="outline" className="ml-2">
                  <span><span key={tUi("order3")}>{tUi("order3")}</span></span>{viewingRawOrderId}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] mt-4">
            <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
              {rawOrderData ? JSON.stringify(rawOrderData, null, 2) : tCommonUi("loading")}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <OrderDetailDrawer
        orderId={detailOrderId}
        integrationId={drawerIntegrationId ?? ""}
        open={!!detailOrderId && !!drawerIntegrationId}
        timezone={userTimezone}
        onOpenChange={(open) => {
          if (!open) {
            setDetailOrderId(null)
            setDetailIntegrationId(null)
            setDetailShop(null)
            // Remove orderId from URL
            const url = new URL(window.location.href)
            url.searchParams.delete("orderId")
            url.searchParams.delete("integrationId")
            url.searchParams.delete("shop")
            window.history.replaceState({}, "", url.toString())
          }
        }}
      />
    </div>
  )
}

function emptyStateHeading(tUi: ReturnType<typeof useTranslations<"Shopify.ShopifyOrdersPage">>, sync: SyncPartState | null): string {
  switch (sync?.status) {
    case "error":
      return tUi("syncFailed")
    case "running":
    case "pending":
      return tUi("syncingOrders")
    default:
      return tUi("noOrdersFound")
  }
}
